import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Avatar, AvatarFallback } from './avatar.js';
import { Button } from './button.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

export interface AppShellNavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  disabled?: boolean;
  disabledTooltip?: string;
  badge?: string | number;
}

export interface AppShellNavLinkProps {
  className: string;
  children: React.ReactNode;
  'aria-label': string;
  'aria-current': 'page' | undefined;
  'data-testid': string;
}

export interface AppShellLabels {
  sidebar: string;
  mainMenu: string;
  expand: string;
  collapse: string;
}

export interface AppShellLayoutProps {
  brandMark: string;
  appName: string;
  navItems: readonly AppShellNavItem[];
  activePath: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  labels: AppShellLabels;
  renderLink: (item: AppShellNavItem, linkProps: AppShellNavLinkProps) => React.ReactNode;
  pageTitle: string;
  topBarActions: React.ReactNode;
  mainStyle?: React.CSSProperties;
  children: React.ReactNode;
}

interface NavItemViewProps {
  item: AppShellNavItem;
  active: boolean;
  collapsed: boolean;
  renderLink: AppShellLayoutProps['renderLink'];
}

function NavItemContent({ item, active, collapsed }: Omit<NavItemViewProps, 'renderLink'>) {
  const Icon = item.icon;
  return (
    <>
      <Icon className="size-4.5 shrink-0" strokeWidth={1.8} />
      {collapsed ? (
        <>
          <span className="text-[9px] leading-tight text-center w-full opacity-70 line-clamp-2 break-words">
            {item.label}
          </span>
          {item.badge != null && (
            <span
              className={cn(
                'text-[8px] font-medium leading-none rounded px-1 py-0.5',
                active ? 'bg-white/20 text-primary-foreground' : 'bg-muted text-muted-foreground',
              )}
            >
              {item.badge}
            </span>
          )}
        </>
      ) : (
        <>
          <span className="flex-1 text-left whitespace-nowrap">{item.label}</span>
          {item.badge != null && (
            <span
              className={cn(
                'ml-auto text-[11px] font-medium rounded-md px-2 py-0.5 min-w-5 text-center',
                active ? 'bg-white/20 text-primary-foreground' : 'bg-muted text-muted-foreground',
              )}
            >
              {item.badge}
            </span>
          )}
        </>
      )}
    </>
  );
}

function NavItemView({ item, active, collapsed, renderLink }: NavItemViewProps) {
  const className = cn(
    'flex w-full rounded-lg text-[14px] transition-colors',
    collapsed
      ? 'flex-col items-center justify-center px-1 py-2 gap-0.5'
      : 'flex-row items-center gap-3 px-3 py-2.5',
    active
      ? 'bg-primary text-primary-foreground font-medium'
      : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground font-normal',
  );
  const content = <NavItemContent item={item} active={active} collapsed={collapsed} />;
  const testId = `nav-${item.path.replace(/\//g, '-').replace(/^-/, '')}`;

  if (item.disabled) {
    return (
      <li>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn(className, 'opacity-50 cursor-not-allowed')}
                aria-label={item.label}
                data-testid={testId}
              >
                {content}
              </span>
            </TooltipTrigger>
            <TooltipContent>{item.disabledTooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </li>
    );
  }

  const link = renderLink(item, {
    className,
    children: content,
    'aria-label': item.label,
    'aria-current': active ? 'page' : undefined,
    'data-testid': testId,
  });

  if (!collapsed) return <li>{link}</li>;

  return (
    <li>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </li>
  );
}

export function AppShellLayout({
  brandMark,
  appName,
  navItems,
  activePath,
  collapsed,
  onToggleCollapsed,
  labels,
  renderLink,
  pageTitle,
  topBarActions,
  mainStyle,
  children,
}: AppShellLayoutProps) {
  return (
    <div className="flex h-screen bg-muted/30 text-foreground overflow-hidden">
      <aside
        className={cn(
          'flex flex-col border-r border-sidebar-border bg-sidebar-background transition-all duration-200',
          collapsed ? 'w-20' : 'w-64',
        )}
        aria-label={labels.sidebar}
      >
        {/* Height stays tied to --app-shell-header-h so the logo box's bottom edge lines up with the top bar's bottom border. */}
        <div className="flex items-center gap-3 px-4 h-(--app-shell-header-h) shrink-0">
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarFallback className="bg-primary text-2xs font-bold text-primary-foreground">
              {brandMark}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <span className="text-[15px] font-semibold tracking-tight whitespace-nowrap text-foreground">
              {appName}
            </span>
          )}
        </div>

        <nav className="flex-1 py-2 overflow-y-auto" aria-label={labels.mainMenu}>
          <ul className="space-y-0.5 px-2">
            {navItems.map((item) => (
              <NavItemView
                key={item.path}
                item={item}
                active={activePath === item.path}
                collapsed={collapsed}
                renderLink={renderLink}
              />
            ))}
          </ul>
        </nav>

        <div className="border-t border-sidebar-border p-2 shrink-0">
          <Button
            variant="ghost"
            type="button"
            size="xs"
            fullWidth
            onClick={onToggleCollapsed}
            data-testid="sidebar-toggle"
            aria-label={collapsed ? labels.expand : labels.collapse}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4 mx-auto" strokeWidth={1.8} />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" strokeWidth={1.8} />
                <span>{labels.collapse}</span>
              </>
            )}
          </Button>
        </div>
      </aside>

      <div className="flex flex-col flex-1 min-w-0">
        <header className="flex items-center justify-between h-(--app-shell-header-h) px-6 border-b border-border/40 bg-background shrink-0">
          <h1 className="text-[17px] font-semibold text-foreground" data-testid="page-title">
            {pageTitle}
          </h1>
          <div className="flex items-center gap-4">{topBarActions}</div>
        </header>

        <main className="flex-1 overflow-y-auto" style={mainStyle}>
          {children}
        </main>
      </div>
    </div>
  );
}
